import { useCallback, useEffect, useState } from 'react';

declare global {
  interface Window {
    $crisp: any[];
    CRISP_WEBSITE_ID: string;
    CrispChat?: {
      openChat: () => void;
      closeChat: () => void;
      hideChat: () => void;
      showChat: () => void;
      setUserEmail: (email: string) => void;
      setUserNickname: (nickname: string) => void;
      setSessionData: (key: string, value: string) => void;
    };
  }
}

export const useCrispChat = () => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Check if Crisp is ready
    const checkCrisp = setInterval(() => {
      if (window.$crisp && window.$crisp.push) {
        setIsReady(true);
        clearInterval(checkCrisp);
      }
    }, 100);

    return () => clearInterval(checkCrisp);
  }, []);

  const openChat = useCallback(() => {
    if (window.CrispChat?.openChat) {
      window.CrispChat.openChat();
    } else if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['do', 'chat:open']);
    }
  }, []);

  const closeChat = useCallback(() => {
    if (window.CrispChat?.closeChat) {
      window.CrispChat.closeChat();
    } else if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['do', 'chat:close']);
    }
  }, []);

  const hideChat = useCallback(() => {
    if (window.CrispChat?.hideChat) {
      window.CrispChat.hideChat();
    } else if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['do', 'chat:hide']);
    }
  }, []);

  const showChat = useCallback(() => {
    if (window.CrispChat?.showChat) {
      window.CrispChat.showChat();
    } else if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['do', 'chat:show']);
    }
  }, []);

  const setUserEmail = useCallback((email: string) => {
    if (window.CrispChat?.setUserEmail) {
      window.CrispChat.setUserEmail(email);
    } else if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['set', 'user:email', [email]]);
    }
  }, []);

  const setUserNickname = useCallback((nickname: string) => {
    if (window.CrispChat?.setUserNickname) {
      window.CrispChat.setUserNickname(nickname);
    } else if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['set', 'user:nickname', [nickname]]);
    }
  }, []);

  const setSessionData = useCallback((key: string, value: string) => {
    if (window.CrispChat?.setSessionData) {
      window.CrispChat.setSessionData(key, value);
    } else if (window.$crisp && window.$crisp.push) {
      window.$crisp.push(['set', 'session:data', [[key, value]]]);
    }
  }, []);

  const setUserInfo = useCallback((userInfo: { email?: string; nickname?: string; [key: string]: any }) => {
    if (window.$crisp && window.$crisp.push) {
      if (userInfo.email) {
        setUserEmail(userInfo.email);
      }
      if (userInfo.nickname) {
        setUserNickname(userInfo.nickname);
      }
      
      // Set additional session data
      Object.entries(userInfo).forEach(([key, value]) => {
        if (key !== 'email' && key !== 'nickname' && typeof value === 'string') {
          setSessionData(key, value);
        }
      });
    }
  }, [setUserEmail, setUserNickname, setSessionData]);

  return {
    isReady,
    openChat,
    closeChat,
    hideChat,
    showChat,
    setUserEmail,
    setUserNickname,
    setSessionData,
    setUserInfo
  };
};
